import { ClientConfig } from '../config/ClientConfig'
import { checkCookie, getCookie } from './CookieStorageService'
import { PromoPlayBlockedMarket } from '../config/FeatureConfig'

export enum RestrictionSource {
	Residential = 'residential',
	Geolocation = 'geolocation',
}

export type PromoPlayPhaseInfo =
	| { enabled: false; phase: PromoPlayPhase }
	| { enabled: true; reason: RestrictionSource; market: PromoPlayBlockedMarket; phase: PromoPlayPhase }

export enum PromoPlayPhase {
	Phase1 = 'phase1',
	Phase2 = 'phase2',
	Phase3 = 'phase3',
}

export class PromoPlayService {
	constructor(
		private now: () => number = () => Date.now(),
	) {}

	isEnabled(): boolean {
		return ClientConfig.PROMO_PLAY_BLOCKED_MARKETS_ENABLED
			|| checkCookie('PROMO_PLAY_BLOCKED_MARKETS_ENABLED')
	}

	isPromoOnlyMarket(residentialState: string, geoLocationState: string): boolean {
		if (!this.isEnabled()) return false
		if (!residentialState && !geoLocationState) return false

		return ClientConfig.PROMO_PLAY_BLOCKED_MARKETS.some(market =>
			(residentialState && market.knownEntries.includes(residentialState.toLowerCase())) ||
			(geoLocationState && market.knownEntries.includes(geoLocationState.toLowerCase()))
		)
	}

	getPhaseInfo(
		phase: PromoPlayPhase,
		residentialState: string,
		geoLocationState: string,
	): PromoPlayPhaseInfo {
		return this._getPromoPlayPhaseInfo(
			phase,
			residentialState,
			geoLocationState,
			(now, market) => {
				const start1 = this.getTimestampOfBlockStart(market)
				const start2 = this.getTimestampOfGameplayBlockStart(market)
				const start3 = this.getTimestampOfPromoPlayPhase3BlockStart(market)

				switch (phase) {
					case PromoPlayPhase.Phase1:
						return start1 <= now && now < start2
					case PromoPlayPhase.Phase2:
						return start2 <= now && now < start3
					case PromoPlayPhase.Phase3:
						return start3 <= now
				}
			}
		)
	}

	isWithin60DaysOfPhase3(residentialState: string, geoLocationState: string): boolean {
		const info = this.getPhaseInfo(PromoPlayPhase.Phase3, residentialState, geoLocationState)
		if (!info.enabled) return false

		const phase3Start = this.getTimestampOfPromoPlayPhase3BlockStart(info.market)
		const displayCutOff = new Date(phase3Start).setDate(60)
		return this.now() <= displayCutOff
	}

	isAnyPhaseActive(residentialState: string, geoLocationState: string): boolean {
		if (!this.isEnabled()) return false
		if (!residentialState && !geoLocationState) return false

		// Check phases in order (most restrictive first) - phases are mutually exclusive
		const phase3Info = this.getPhaseInfo(PromoPlayPhase.Phase3, residentialState, geoLocationState)
		if (phase3Info.enabled) return true

		const phase2Info = this.getPhaseInfo(PromoPlayPhase.Phase2, residentialState, geoLocationState)
		if (phase2Info.enabled) return true

		const phase1Info = this.getPhaseInfo(PromoPlayPhase.Phase1, residentialState, geoLocationState)
		return phase1Info.enabled
	}

	isPhase2OrLaterActive(residentialState: string, geoLocationState: string): boolean {
		if (!this.isEnabled()) return false
		if (!residentialState && !geoLocationState) return false

		const phase3Info = this.getPhaseInfo(PromoPlayPhase.Phase3, residentialState, geoLocationState)
		if (phase3Info.enabled) return true

		const phase2Info = this.getPhaseInfo(PromoPlayPhase.Phase2, residentialState, geoLocationState)
		return phase2Info.enabled
	}

	private _getPromoPlayPhaseInfo(
		phase: PromoPlayPhase,
		residentialState: string,
		geoLocationState: string,
		isInPhase: (now: number, market: PromoPlayBlockedMarket) => boolean,
	): PromoPlayPhaseInfo {
		if (!this.isEnabled()) return { enabled: false, phase }
		if (!residentialState && !geoLocationState) return { enabled: false, phase }

		const now = this.now()
		for (const market of ClientConfig.PROMO_PLAY_BLOCKED_MARKETS) {
			if (!isInPhase(now, market)) continue

			const lowerGeo = geoLocationState?.toLowerCase()
			const lowerRes = residentialState?.toLowerCase()

			if (lowerGeo && market.knownEntries.includes(lowerGeo)) {
				return { enabled: true, reason: RestrictionSource.Geolocation, market, phase }
			}
			if (lowerRes && market.knownEntries.includes(lowerRes)) {
				return { enabled: true, reason: RestrictionSource.Residential, market, phase }
			}
		}
		return { enabled: false, phase }
	}

	private getTimestampOfBlockStart(market: PromoPlayBlockedMarket): number {
		const cookie = getCookie(`P1_BLOCK_${market.isoEntry}_START_TIME`)
		return cookie ? Date.parse(cookie.value) : market.blockDate
	}

	private getTimestampOfGameplayBlockStart(market: PromoPlayBlockedMarket): number {
		const cookieVal = getCookie(`P2_BLOCK_${market.isoEntry}_START_TIME`)?.value
		if (cookieVal) return Date.parse(cookieVal)

		const d = new Date(this.getTimestampOfBlockStart(market))
		return d.setDate(d.getDate() + market.blockAllPlayAfterDays)
	}

	private getTimestampOfPromoPlayPhase3BlockStart(market: PromoPlayBlockedMarket): number {
		const cookieVal = getCookie(`P3_BLOCK_${market.isoEntry}_START_TIME`)?.value
		if (cookieVal) return Date.parse(cookieVal)
		if (market.blockRedemptionsDate !== undefined) return market.blockRedemptionsDate

		const d = new Date(this.getTimestampOfBlockStart(market))
		return d.setDate(d.getDate() + market.blockRedemptionsAfterDays)
	}
}
